// SPDX-FileCopyrightText: 2026 Ebiose AI
// SPDX-License-Identifier: LicenseRef-Ebiose-Proprietary
// All rights reserved. Confidential — unauthorized distribution prohibited.

// Single decision point for cycle-dashboard query freshness (PRD §Modules
// `refresh-policy`; issue `refresh-policy-live-polling`).
//
// Every polling literal lives here so the future push doorbell is a one-place
// flip (US §28): callers ask this module for `refetchInterval` instead of
// hard-coding a number, and reuse `REFETCH_IN_BACKGROUND` instead of setting
// their own literal.
//
// Contract (PRD §Interfaces):
//   - input: query kind + latest known CycleStatus (ADR-0022, single-sourced —
//     this module never re-derives it).
//   - output: TanStack-compatible `number | false` interval.
//   - terminal status (`completed` / `failed` / `expired`) ⇒ `false` for every
//     kind (INV-2, INV-10).
//   - `running` or status not yet in cache (`undefined`) ⇒ bounded interval —
//     a query never freezes because its own detail fetch hasn't landed yet.
//   - `list` kind has a second decision point (`listIntervalFor`): an unfiltered
//     list keys on its rows — once every row is terminal (or list is empty),
//     nothing on screen can change, so polling stops (DEV-416 follow-up; the
//     pre-fix behaviour polled unfiltered lists forever).

export type QueryKind =
  | "detail"
  | "leaderboard"
  | "events"
  | "snapshot"
  | "list"

// Seconds-scale per PRD §Interfaces; not per-kind today. `kind` stays in
// `intervalFor`'s signature so a future differentiated cadence is a change
// inside this module only.
export const LIVE_REFETCH_INTERVAL_MS = 5_000

// Hidden tabs keep polling (US §3) — the flag is owned here, once.
export const REFETCH_IN_BACKGROUND = true

// Three terminal statuses per ADR-0022; `running` and `undefined` are not.
export const isTerminal = (status: CycleStatus | undefined): boolean =>
  status === "completed" || status === "failed" || status === "expired"

export const intervalFor = (
  _kind: QueryKind,
  status: CycleStatus | undefined,
): number | false => (isTerminal(status) ? false : LIVE_REFETCH_INTERVAL_MS)

// Two-signal decision for the `list` kind:
//   1. caller's `?status=` filter — terminal filter stops; `running` filter
//      stays live regardless of rows (new matching cycles must surface).
//   2. no filter ⇒ rows — stops once every row is terminal (empty list
//      included); new cycles surface via TanStack mount/window-focus refetches
//      instead of a forever-armed 5 s interval (DEV-416 follow-up).
// `itemStatuses === undefined` keeps the interval armed — "never freeze before
// first fetch lands", same posture as `intervalFor`.
export const listIntervalFor = (
  statusFilter: CycleStatus | undefined,
  itemStatuses: ReadonlyArray<CycleStatus> | undefined,
): number | false => {
  if (statusFilter !== undefined) return intervalFor("list", statusFilter)
  if (itemStatuses !== undefined && itemStatuses.every(isTerminal)) {
    return false
  }
  return LIVE_REFETCH_INTERVAL_MS
}

// Structural slice kept local (not imported from wire types or TanStack's Query)
// so this module stays dependency-free.
type ListStatusSource = { items: ReadonlyArray<{ status: CycleStatus }> }

// Uses TanStack's function form for `refetchInterval` so it re-evaluates
// against the query's own latest data — the hook has no earlier access to rows.
export const listPollingOptionsFor = (
  statusFilter: CycleStatus | undefined,
): {
  refetchInterval: (query: {
    state: { data?: ListStatusSource }
  }) => number | false
  refetchIntervalInBackground: boolean
} => ({
  refetchInterval: (query) =>
    listIntervalFor(
      statusFilter,
      query.state.data?.items.map((item) => item.status),
    ),
  refetchIntervalInBackground: REFETCH_IN_BACKGROUND,
})

// Computed together so a call site can never set one polling option without
// the other.
export const pollingOptionsFor = (
  kind: QueryKind,
  status: CycleStatus | undefined,
): {
  refetchInterval: number | false
  refetchIntervalInBackground: boolean
} => ({
  refetchInterval: intervalFor(kind, status),
  refetchIntervalInBackground: REFETCH_IN_BACKGROUND,
})
